import mitt from 'mitt';
import { Component } from './component';

export interface WindowProps {
  parent?: Window;
  title?: string;
  resizable?: boolean;
  width: number;
  height: number;
  position?: { x: number; y: number };
  hidden?: boolean;
  static?: boolean;
}

type Events = {
  focused: undefined;
};

export class Window extends Component {
  public el: HTMLDivElement;
  public body: HTMLDivElement;
  public parent: Window | null = null;

  private dragAnchor = { x: 0, y: 0 };
  private pointerId: number | null = null;
  private boundOnPointerMove: (e: PointerEvent) => void;
  private boundOnPointerUp: (e: PointerEvent) => void;
  private boundOnPointerCancel: (e: PointerEvent) => void;
  private emitter = mitt<Events>();

  constructor(props: WindowProps) {
    super();

    this.boundOnPointerMove = this.onPointerMove.bind(this);
    this.boundOnPointerUp = this.onPointerUp.bind(this);
    this.boundOnPointerCancel = this.onPointerCancel.bind(this);

    this.el = document.createElement('div');
    this.el.classList.add('window');
    this.el.style.position = 'absolute';

    this.el.addEventListener('pointerdown', (e) => {
      if (
        (e.button !== 0 && e.pointerType !== 'touch') ||
        !titleBar.classList.contains('inactive')
      )
        return;
      this.emitter.emit('focused', undefined);
    });

    if (props.hidden) {
      this.el.style.display = 'none';
    }

    const titleBar = document.createElement('div');
    titleBar.classList.add('title-bar');

    const titleBarText = document.createElement('div');
    titleBarText.classList.add('title-bar-text');

    if (props.title) {
      titleBarText.textContent = props.title;
    }

    const titleBarControls = document.createElement('div');
    titleBarControls.classList.add('title-bar-controls');

    if (!props.static) {
      const minimizeButton = document.createElement('button');
      minimizeButton.setAttribute('aria-label', 'Minimize');
      titleBarControls.appendChild(minimizeButton);
    }

    if (!props.static && (props.resizable || props.resizable === undefined)) {
      const maximizeRestoreButton = document.createElement('button');
      maximizeRestoreButton.setAttribute('aria-label', 'Maximize');
      titleBarControls.appendChild(maximizeRestoreButton);
    }

    const closeButton = document.createElement('button');
    closeButton.setAttribute('aria-label', 'Close');
    titleBarControls.appendChild(closeButton);

    titleBar.appendChild(titleBarText);
    titleBar.appendChild(titleBarControls);
    this.el.appendChild(titleBar);

    this.body = document.createElement('div');
    this.body.classList.add('window-body');
    this.el.appendChild(this.body);

    this.body.style.width = `${props.width}px`;
    this.body.style.height = `${props.height}px`;

    if (!props.static) {
      titleBar.addEventListener('pointerdown', this.onPointerDown.bind(this));
    }

    if (props.parent) {
      this.parent = props.parent;
    }

    if (props.position) {
      this.el.style.left = `${props.position.x}px`;
      this.el.style.top = `${props.position.y}px`;
    }
  }

  on<K extends keyof Events>(type: K, handler: (event: Events[K]) => void) {
    this.emitter.on(type, handler);
  }

  setActive(active: boolean) {
    const titleBar = this.el.querySelector('.title-bar');
    if (!titleBar) return;

    if (active) {
      titleBar.classList.remove('inactive');
    } else {
      titleBar.classList.add('inactive');
    }
  }

  getZIndex(): number {
    return parseInt(this.el.style.zIndex || '0', 10);
  }

  setZIndex(zIndex: number) {
    this.el.style.zIndex = zIndex.toString();
  }

  private onPointerDown(e: PointerEvent) {
    if (e.button !== 0 && e.pointerType !== 'touch') return;

    (e.target as HTMLElement).setPointerCapture(e.pointerId);

    this.dragAnchor.x = e.clientX - this.el.offsetLeft;
    this.dragAnchor.y = e.clientY - this.el.offsetTop;

    this.pointerId = e.pointerId;

    window.addEventListener('pointermove', this.boundOnPointerMove, {
      passive: false,
    });
    window.addEventListener('pointerup', this.boundOnPointerUp, {
      passive: false,
    });
    window.addEventListener('pointercancel', this.boundOnPointerCancel, {
      passive: false,
    });
  }

  private onPointerMove(e: PointerEvent) {
    if (e.pointerId !== this.pointerId) return;

    this.el.style.left = `${e.clientX - this.dragAnchor.x}px`;
    this.el.style.top = `${e.clientY - this.dragAnchor.y}px`;

    e.preventDefault();
  }

  private onPointerUp(e: PointerEvent) {
    if (e.pointerId !== this.pointerId) return;

    this.teardownPointerEvents();
  }

  private onPointerCancel(_e: PointerEvent) {
    this.teardownPointerEvents();
  }

  private teardownPointerEvents() {
    window.removeEventListener('pointermove', this.boundOnPointerMove);
    window.removeEventListener('pointerup', this.boundOnPointerUp);
    window.removeEventListener('pointercancel', this.boundOnPointerCancel);
  }
}
